import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { getSessionUser, isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';
import AlertDialog from '../../Shared/AlertDialog';

/**
 * BulkCursoTab: 
 * Maneja la lógica de asignación masiva para Áreas, Puestos o Turnos.
 * @param {string} type - 'area', 'puesto' o 'turno'
 * @param {number} fkId - El ID del área, puesto o turno seleccionado
 */
export default function BulkCursoTab({ type, fkId, onClose }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [allCourses, setAllCourses] = useState([]);
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [action, setAction] = useState('ASIGNAR'); // 'ASIGNAR' o 'DESASIGNAR'
    const [query, setQuery] = useState('');
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        // El usuario dijo que se cierre el modal primero
        if (onClose) onClose();
        setAlertConfig({ open: true, message, variant, title });
    };

    const loadCourses = async () => {
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.cursos);
            const data = (res.data || []).map(Models.cursos);
            setAllCourses(data);
        } catch (error) {
            console.error('Error al cargar cursos:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCourses();
    }, []);

    // Ids de cursos seleccionados
    const selectedIds = useMemo(() => new Set(selectedCourses.map(c => c.idCurso)), [selectedCourses]);

    // Cursos disponibles (no seleccionados)
    const availableCourses = useMemo(() => {
        const isSuperAdmin = isSessionSuperAdmin();
        const sessionEmpresaId = getSessionEmpresaId();

        let available = allCourses.filter(c => {
            // 1. No estar seleccionado ya
            if (selectedIds.has(c.idCurso)) return false;
            // 2. Estar activo y terminado
            if (!c.activo || !c.terminado) return false;

            // 3. Reglas multi-tenant:
            if (isSuperAdmin) return true;

            return (
                c.idEmpresa === sessionEmpresaId ||
                c.compartido === true ||
                c.autoAsignable === true
            );
        });

        // Filter by query
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            available = available.filter(c => 
                c.nombre.toLowerCase().includes(q) || 
                (c.empresaNombre || '').toLowerCase().includes(q)
            );
        }
        return available;
    }, [allCourses, selectedIds, query]);

    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    };

    const handleDragOver = (e, target) => {
        e.preventDefault();
        setDragOverColumn(target);
    };

    const handleDrop = (e, target) => {
        e.preventDefault();
        setDragOverColumn(null);
        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);
        if (source === target) return;

        if (target === 'selected') {
            setSelectedCourses(prev => [...prev, item]);
        } else {
            setSelectedCourses(prev => prev.filter(c => c.idCurso !== item.idCurso));
        }
    };

    const handleSave = async () => {
        if (selectedCourses.length === 0) {
            triggerAlert('Por favor selecciona al menos un curso.');
            return;
        }

        const confirmMsg = action === 'ASIGNAR'
            ? `¿Estás seguro de que deseas INSCRIBIR masivamente a todos los ESTUDIANTES de este ${type} en los cursos seleccionados?`
            : `¿Estás seguro de que deseas DAR DE BAJA masivamente a todos los ESTUDIANTES de este ${type} de los cursos seleccionados?`;

        setAlertConfig({
            open: true,
            title: 'Confirmar Operación',
            message: confirmMsg,
            variant: 'info',
            onConfirm: async () => {
                setAlertConfig(prev => ({ ...prev, open: false }));
                setSaving(true);
                try {
                    const usuarioActual = getSessionUser() || { idUsuario: 1 };

                    // Elegimos el endpoint según el tipo
                    let endpoint = '';
                    let idParamName = '';
                    if (type === 'area') {
                        endpoint = ENDPOINTS.estudianteCursoAsignarPorArea;
                        idParamName = 'idAreaNegocio';
                    } else if (type === 'puesto') {
                        endpoint = ENDPOINTS.estudianteCursoAsignarPorPuesto;
                        idParamName = 'idPuesto';
                    } else if (type === 'turno') {
                        endpoint = ENDPOINTS.estudianteCursoAsignarPorTurno;
                        idParamName = 'idTurno';
                    }

                    // Ejecutamos una petición por cada curso
                    const promises = selectedCourses.map(course => {
                        const payload = {
                            idUsuarioActual: usuarioActual.idUsuario,
                            idCurso: course.idCurso,
                            [idParamName]: fkId,
                            accion: action
                        };
                        return axios.post(endpoint, payload);
                    });

                    await Promise.all(promises);
                    triggerAlert('Operación masiva completada con éxito.', 'success', 'Éxito');
                } catch (error) {
                    console.error('Error en la operación masiva:', error);
                    triggerAlert('Ocurrió un error al procesar la asignación masiva.');
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    return (
        <div className="mini-crud">
            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '20px', background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <span style={{ fontWeight: 600 }}>Acción a realizar:</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="radio"
                        name="bulkAction"
                        value="ASIGNAR"
                        checked={action === 'ASIGNAR'}
                        onChange={(e) => setAction(e.target.value)}
                    />
                    Inscribir Masivamente
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="radio"
                        name="bulkAction"
                        value="DESASIGNAR"
                        checked={action === 'DESASIGNAR'}
                        onChange={(e) => setAction(e.target.value)}
                    />
                    Dar de Baja Masivamente
                </label>
            </div>

            <div style={{ padding: '0 0.5rem 1rem 0.5rem' }}>
                <input
                    className="roles-search"
                    placeholder="Buscar curso disponible..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    style={{ width: '100%', marginBottom: 0 }}
                />
            </div>

            <div className="dnd-container" style={{ height: '400px' }}>
                {/* Disponibles */}
                <div
                    className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, 'available')}
                    onDrop={(e) => handleDrop(e, 'available')}
                >
                    <div className="dnd-header">Cursos Disponibles</div>
                    {loading ? (
                        <div className="dnd-empty">Cargando cursos...</div>
                    ) : availableCourses.length === 0 ? (
                        <div className="dnd-empty">No hay más cursos</div>
                    ) : (
                        availableCourses.map(course => (
                            <div
                                key={course.idCurso}
                                className="dnd-item"
                                draggable
                                onDragStart={(e) => handleDragStart(e, course, 'available')}
                            >
                                <span>{course.nombre}</span>
                                <button type="button" onClick={() => setSelectedCourses(prev => [...prev, course])} style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer' }}>＋</button>
                            </div>
                        ))
                    )}
                </div>

                {/* Seleccionados */}
                <div
                    className={`dnd-column ${dragOverColumn === 'selected' ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, 'selected')}
                    onDrop={(e) => handleDrop(e, 'selected')}
                >
                    <div className="dnd-header">Cursos a Procesar</div>
                    {selectedCourses.length === 0 ? (
                        <div className="dnd-empty">Arrastra cursos aquí</div>
                    ) : (
                        selectedCourses.map(course => (
                            <div
                                key={course.idCurso}
                                className="dnd-item"
                                draggable
                                onDragStart={(e) => handleDragStart(e, course, 'selected')}
                            >
                                <span>{course.nombre}</span>
                                <button type="button" onClick={() => setSelectedCourses(prev => prev.filter(c => c.idCurso !== course.idCurso))} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}>×</button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.5rem' }}>
                <button className="roles-btn" onClick={onClose} type="button">Cancelar</button>
                <button
                    className="btn-action"
                    onClick={handleSave}
                    disabled={saving || selectedCourses.length === 0}
                    style={{ minWidth: '150px' }}
                >
                    {saving ? 'Procesando...' : 'Ejecutar Acción Masiva'}
                </button>
            </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
